import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAtomValue } from 'jotai'
import { useTranslate } from '@repo/unicity'
import {
    ActionButton,
    ErrorMessages,
    renderFieldRows,
    CheckoutBreadcrumb,
    LoginBar,
    buildFieldsFromSettings,
    mergeSettingsInputs,
    type FieldRenderContext,
    type BreadcrumbStep
} from '@/core/components'
import { useCurrentLanguage } from '@/core/utils'
import CheckoutLayoutSkeleton from '@/core/checkout/components/checkout-layout-skeleton.component'
import { ReCaptchaV2Modal } from '@/core/recaptcha/recaptcha-v2-modal.component'
import { useRecaptchaGate } from '@/core/recaptcha/use-recaptcha-gate.hook'
import { feelgreatCountryAtom } from '@/core/store/country.store'
import { fgUseComplexPasswordAtom, fgCreateAccountInputsAtom, fgShippingAddressInputsAtom } from '@/core/feelgreat/feelgreat.store'
import { taiwanCityOptions } from '../../taiwan.cities'
import {
    guestAgreementFields,
    taiwanConfig,
    taiwanGuestFieldOptions,
    taiwanMemberFieldOptions,
    taiwanGuestFieldsFallback,
    taiwanMemberFieldsFallback
} from '../../taiwan.config'
import { useTaiwanCheckout } from '../../hooks/useTaiwanCheckout'
import TaiwanOrderSummary from './components/TaiwanOrderSummary'

// Figma 26:147936 (desktop) / 26:151119 (mobile) — white card, radius 12,
// no border. Padding 20 on mobile, 30 from the desktop breakpoint.
const SectionCard = styled(Box)`
    border-radius: 12px;
    padding: 20px;
    background: #ffffff;

    @media (min-width: 1024px) {
        padding: 30px;
    }
`

// Figma 26:147936 — content row: form 770 + summary 380, column-gap 30,
// row-gap 20 (login→form), max 1180. Cards stack with 20 gap on mobile.
const LayoutGrid = styled(Box)`
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    max-width: 1180px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
    padding: 24px 20px;
    @media (min-width: 1024px) {
        grid-template-columns: minmax(0, 1fr) 380px;
        gap: 20px 30px;
        align-items: start;
    }
`

// Taiwan is a 4-step flow — passed to the skeleton so its stepper matches the
// loaded one (avoids a 3↔4 jump while the format loads).
const TAIWAN_SKELETON_STEPS = [
    { key: 'customise', label: 'customise' },
    { key: 'shipping', label: 'shipping' },
    { key: 'billing', label: 'billing' },
    { key: 'purchase', label: 'purchase' }
]

// Breadcrumb targets. Step 1 (Shipping) resolves to the auth-specific route so
// the URL stays correct: /checkout/shipping for members, /checkout/create-account
// for guests (not the generic /checkout). Only past/active steps are clickable.
// Carries the checkout uuid on back-navigation for every step except Customise.
const buildStepPath = (index: number, uuid: string | null, isAuthenticated: boolean): string => {
    const paths = ['/products', isAuthenticated ? '/checkout/shipping' : '/checkout/create-account', '/checkout/payment', '/checkout/review']
    const path = paths[index] ?? '/'
    return index !== 0 && uuid ? `${path}?uuid=${uuid}` : path
}

export default function TaiwanCheckoutModule() {
    const { translate } = useTranslate()
    const language = useCurrentLanguage()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const fgCountry = useAtomValue(feelgreatCountryAtom)
    const useComplexPassword = useAtomValue(fgUseComplexPasswordAtom)
    const {
        isAuthenticated,
        accountForm,
        setAccountForm,
        shippingForm,
        setShippingForm,
        submitGuest,
        submitMember,
        handleLoginSuccess,
        init,
        initialized,
        loading,
        errors
    } = useTaiwanCheckout()

    const [showPassword, setShowPassword] = useState(false)
    const { isVerifying: recaptchaVerifying, runWithRecaptcha, modalProps: recaptchaModalProps } = useRecaptchaGate()
    const checkoutRecaptchaVerifying = !isAuthenticated && recaptchaVerifying

    useEffect(() => {
        init()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const regions = useMemo(() => taiwanCityOptions(language).map(c => ({ name: c.text, id: c.value })), [language])

    const createInputs = useAtomValue(fgCreateAccountInputsAtom)
    const shippingInputs = useAtomValue(fgShippingAddressInputsAtom)

    const form = isAuthenticated ? shippingForm : accountForm
    const setForm = (isAuthenticated ? setShippingForm : setAccountForm) as unknown as React.Dispatch<React.SetStateAction<Record<string, unknown>>>
    // Cached field handlers close over this ref (not `setForm` directly) so that
    // when auth flips guest→member the shared-key handlers dispatch to the member
    // setter instead of the stale guest one (otherwise the member form is frozen).
    const setFormRef = useRef(setForm)
    setFormRef.current = setForm

    // Fields are server-driven per country: logged-in = shippingAddress; guest =
    // createYourAccount ∪ shippingAddress. Fall back to the static layout only if
    // settings haven't loaded yet.
    const fields = useMemo(() => {
        if (isAuthenticated) {
            const built = buildFieldsFromSettings(shippingInputs, taiwanMemberFieldOptions)
            return built.length > 0 ? built : taiwanMemberFieldsFallback
        }
        const built = buildFieldsFromSettings(mergeSettingsInputs(createInputs, shippingInputs), taiwanGuestFieldOptions)
        return built.length > 0 ? built : taiwanGuestFieldsFallback
    }, [isAuthenticated, createInputs, shippingInputs])

    const handlersRef = useRef<Record<string, (e: React.ChangeEvent<HTMLInputElement>) => void>>({})
    const set = (key: string) => {
        if (!handlersRef.current[key]) {
            handlersRef.current[key] = e => setFormRef.current(prev => ({ ...prev, [key]: e.target.value }))
        }
        return handlersRef.current[key]
    }

    const fieldCtx: FieldRenderContext = useMemo(
        () => ({
            form: form as unknown as Record<string, unknown>,
            setForm,
            set,
            formDisabled: loading || checkoutRecaptchaVerifying,
            regions,
            countryDisplayName: 'Taiwan',
            countryFlagUrl: fgCountry?.flagUrl,
            showPassword,
            setShowPassword,
            useComplexPassword,
            onLinkClick: () => {},
            testIdPrefix: 'taiwan-checkout'
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [form, loading, checkoutRecaptchaVerifying, regions, fgCountry?.flagUrl, showPassword, useComplexPassword, language]
    )

    const formRecord = form as unknown as Record<string, unknown>
    const agreementsOk = isAuthenticated || (formRecord.pdpa === true && formRecord.terms === true)
    const submitDisabled = loading || checkoutRecaptchaVerifying || !agreementsOk

    const runCheckoutSubmit = useCallback(
        async (kind: 'guest' | 'member') => {
            if (kind === 'member') await submitMember()
            else await submitGuest()
        },
        [submitGuest, submitMember]
    )

    const runSubmitWithRecaptcha = useCallback(
        async (kind: 'guest' | 'member') => {
            if (kind === 'member') {
                await runCheckoutSubmit(kind)
                return
            }
            await runWithRecaptcha('checkout', () => runCheckoutSubmit(kind), taiwanConfig.features.checkout.recaptcha)
        },
        [runCheckoutSubmit, runWithRecaptcha]
    )

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (submitDisabled) return
        void runSubmitWithRecaptcha(isAuthenticated ? 'member' : 'guest')
    }

    const steps: BreadcrumbStep[] = useMemo(
        () => [
            { key: 'customise', label: translate('customise') },
            { key: 'shipping', label: translate(isAuthenticated ? 'shipping' : 'signup_shipping') },
            { key: 'billing', label: translate('billing') },
            { key: 'purchase', label: translate('purchase') }
        ],
        [translate, isAuthenticated]
    )

    // Hold the skeleton until the initial detail-v2 format fetch settles.
    if (!initialized) {
        return <CheckoutLayoutSkeleton steps={TAIWAN_SKELETON_STEPS} data-testid="taiwan-checkout-skeleton" />
    }

    return (
        <Box sx={{ width: '100%' }}>
            {!isAuthenticated && <ReCaptchaV2Modal {...recaptchaModalProps} />}

            <CheckoutBreadcrumb
                steps={steps}
                activeIndex={1}
                onStepClick={index => navigate(buildStepPath(index, searchParams.get('uuid'), isAuthenticated))}
                sx={{ pt: '40px' }}
                data-testid="taiwan-checkout-breadcrumb"
            />

            <LayoutGrid>
                {/* Mobile stacking (DOM order): LoginBar → Summary → Form. Desktop
                    (>=1024) places them explicitly: login+form left, summary right. */}
                {!isAuthenticated && (
                    <Box sx={{ '@media (min-width:1024px)': { gridColumn: 1, gridRow: 1 } }} data-testid="taiwan-checkout-login-bar-box">
                        <LoginBar onLoginSuccess={handleLoginSuccess} data-testid="taiwan-checkout-login-bar" />
                    </Box>
                )}

                <Box sx={{ '@media (min-width:1024px)': { gridColumn: 2, gridRow: '1 / span 2' } }} data-testid="taiwan-checkout-order-summary-box">
                    <TaiwanOrderSummary data-testid="taiwan-checkout-order-summary" />
                </Box>

                <Stack
                    spacing={3}
                    component="form"
                    onSubmit={onSubmit}
                    sx={{ '@media (min-width:1024px)': { gridColumn: 1, gridRow: isAuthenticated ? 1 : 2 } }}
                    data-testid="taiwan-checkout-form">
                    <SectionCard data-testid="taiwan-checkout-section">
                        <Typography
                            sx={{ fontSize: '24px', fontWeight: 700, color: '#0b3b61', mb: 3, '@media (min-width:1024px)': { fontSize: '32px' } }}
                            data-testid="taiwan-checkout-title">
                            {translate(isAuthenticated ? 'ship_to' : 'signup_shipping')}
                        </Typography>

                        <Stack spacing={2.5} data-testid="taiwan-checkout-fields">
                            {renderFieldRows(fields, fieldCtx)}
                        </Stack>

                        {!isAuthenticated && (
                            <Stack spacing={1} sx={{ mt: 3 }} data-testid="taiwan-checkout-agreements">
                                {renderFieldRows(guestAgreementFields, fieldCtx)}
                            </Stack>
                        )}

                        <ErrorMessages list={errors} sx={{ mt: 2 }} data-testid="taiwan-checkout-errors" />

                        <Box sx={{ mt: '30px', display: 'flex', justifyContent: 'center' }} data-testid="taiwan-checkout-submit-box">
                            <ActionButton
                                text={loading || checkoutRecaptchaVerifying ? '' : translate(isAuthenticated ? 'continue_to_payment' : 'create_your_account')}
                                type="submit"
                                disabled={submitDisabled}
                                startIcon={loading || checkoutRecaptchaVerifying ? <CircularProgress size={22} color="inherit" /> : undefined}
                                data-testid="taiwan-checkout-submit"
                            />
                        </Box>
                    </SectionCard>
                </Stack>
            </LayoutGrid>
        </Box>
    )
}
